import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import AppHeader from './AppHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatDateKey = (date) => {
  if (!(date instanceof Date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
const formatDayLabel = (date) => date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const getRangeStart = (date, radius) => {
  const start = new Date(date);
  start.setDate(start.getDate() - radius);
  start.setHours(0, 0, 0, 0);
  return start;
};

const EarningsPrediction = ({
  onLogout,
  onShowScanner,
  onShowEarnings,
  onShowTastytrade,
  onShowAdmin,
  isAdmin,
}) => {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [earnings, setEarnings] = useState([]);
  const [earningsWindow, setEarningsWindow] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAccuracy, setShowAccuracy] = useState(false);
  const [accuracyData, setAccuracyData] = useState(null);
  const [timelineDays, setTimelineDays] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [availableSectors, setAvailableSectors] = useState([]);
  const [calendarRadius] = useState(3); // +/- days to show (7-day window)
  const [watchlist, setWatchlist] = useState(() => {
    try {
      const stored = typeof window !== 'undefined' ? window.localStorage.getItem('earnings_watchlist') : null;
      return new Set(stored ? JSON.parse(stored) : []);
    } catch {
      return new Set();
    }
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [validationInputs, setValidationInputs] = useState({});
  const [validationLoading, setValidationLoading] = useState({});
  const [filters, setFilters] = useState({
    sector: 'all',
    marketCap: 'all',
    confidence: 'all',
    dataQuality: 'all',
    validated: 'all',
    watchlistOnly: false,
  });

  useEffect(() => {
    fetchEarningsCalendar(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    const selectedKey = formatDateKey(selectedDate);
    const dayRecords = earningsWindow.filter((earning) => earning.earnings_date?.startsWith(selectedKey));
    setEarnings(dayRecords);
  }, [selectedDate, earningsWindow]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('earnings_watchlist', JSON.stringify(Array.from(watchlist)));
      }
    } catch {
      /* noop */
    }
  }, [watchlist]);

  const buildTimeline = (startDate, totalDays, data) => {
    const days = [];
    const todayKey = formatDateKey(new Date());
    for (let i = 0; i < totalDays; i += 1) {
      const day = new Date(startDate);
      day.setDate(startDate.getDate() + i);
      const key = formatDateKey(day);
      const dayEvents = data.filter((earning) => earning.earnings_date?.startsWith(key));
      days.push({
        date: day,
        key,
        label: formatDayLabel(day),
        total: dayEvents.length,
        highConfidence: dayEvents.filter((e) => (e.confidence_score || 0) >= 70).length,
        validated: dayEvents.filter((e) => e.validated).length,
        isToday: key === todayKey,
      });
    }
    return days;
  };

  const calculateSummary = (data) => {
    if (!data.length) {
      return null;
    }
    const total = data.length;
    const highConfidence = data.filter((e) => (e.confidence_score || 0) >= 70).length;
    const pendingValidation = data.filter((e) => !e.validated).length;
    const averageBeat = (data.reduce((sum, e) => sum + (e.beat_probability || 0), 0) / total).toFixed(1);
    const sectorCounts = data.reduce((acc, earning) => {
      if (!earning.sector) return acc;
      acc[earning.sector] = (acc[earning.sector] || 0) + 1;
      return acc;
    }, {});
    const topSectors = Object.entries(sectorCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([sector, count]) => ({ sector, count }));
    return {
      total,
      highConfidence,
      pendingValidation,
      averageBeat,
      topSectors,
    };
  };

  const fetchEarningsCalendar = async (anchorDate) => {
    setLoading(true);
    setErrorMessage('');
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setErrorMessage('You must be logged in to view earnings calendar');
        setEarnings([]);
        setEarningsWindow([]);
        setLoading(false);
        return;
      }
      
      const center = anchorDate || selectedDate;
      const rangeStart = getRangeStart(center, calendarRadius);
      const startKey = formatDateKey(rangeStart);
      const totalDays = calendarRadius * 2 + 1;
      
      const response = await axios.get(
        `${API}/earnings/calendar?date=${startKey}&days=${totalDays}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const events = (response.data.earnings || []).sort((a, b) => (a.ticker || '').localeCompare(b.ticker || ''));
      setEarningsWindow(events);
      setTimelineDays(buildTimeline(rangeStart, totalDays, events));
      setSummaryStats(calculateSummary(events));
      const sectors = Array.from(new Set(events.map((e) => e.sector).filter(Boolean))).sort();
      setAvailableSectors(sectors);
      const selectedKey = formatDateKey(center);
      setEarnings(events.filter((earning) => earning.earnings_date?.startsWith(selectedKey)));
    } catch (err) {
      console.error('Error fetching earnings:', err);
      if (err.response?.status === 401) {
        setErrorMessage('Session expired. Please login again.');
      } else {
        setErrorMessage(`Error loading earnings: ${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
      }
      setEarnings([]);
      setEarningsWindow([]);
      setTimelineDays([]);
      setSummaryStats(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async (ticker) => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        alert('You must be logged in to view company details');
        return;
      }
      
      const response = await axios.get(
        `${API}/earnings/company/${ticker}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setCompanyDetails(response.data);
      setSelectedCompany(ticker);
    } catch (err) {
      console.error('Error fetching company details:', err);
      alert(`Error loading company details: ${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
    }
  };

  const fetchAccuracyData = async (days = 90) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(
        `${API}/earnings/accuracy?days=${days}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      setAccuracyData(response.data);
    } catch (err) {
      console.error('Error fetching accuracy data:', err);
    }
  };

  useEffect(() => {
    if (showAccuracy && !accuracyData) {
      fetchAccuracyData();
    }
  }, [showAccuracy]);

  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const getPredictionBadge = (direction, probability) => {
    const colors = {
      BEAT: 'bg-green-100 text-green-800',
      MISS: 'bg-red-100 text-red-800',
      INLINE: 'bg-yellow-100 text-yellow-800',
    };

    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${colors[direction] || 'bg-gray-100 text-gray-600'}`}>
        {direction} ({probability ? `${probability.toFixed(0)}%` : 'N/A'})
      </span>
    );
  };

  const toggleRowExpansion = (ticker) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) {
        next.delete(ticker);
      } else {
        next.add(ticker);
      }
      return next;
    });
  };

  const toggleWatchlistTicker = (ticker) => {
    setWatchlist((prev) => {
      const next = new Set(prev);
      if (next.has(ticker)) {
        next.delete(ticker);
      } else {
        next.add(ticker);
      }
      return next;
    });
  };

  const handleValidationInputChange = (ticker, field, value) => {
    setValidationInputs((prev) => ({
      ...prev,
      [ticker]: {
        ...(prev[ticker] || {}),
        [field]: value,
      },
    }));
  };

  const handleValidationSubmit = async (ticker) => {
    const payload = validationInputs[ticker];
    if (!payload?.actual_eps) {
      alert('Actual EPS is required');
      return;
    }
    try {
      setValidationLoading((prev) => ({ ...prev, [ticker]: true }));
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/earnings/validate/${ticker}`,
        {
          actual_eps: parseFloat(payload.actual_eps),
          actual_revenue: payload.actual_revenue ? parseFloat(payload.actual_revenue) : undefined,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      alert(`Actuals recorded for ${ticker}`);
      fetchEarningsCalendar(selectedDate);
      setValidationInputs((prev) => ({
        ...prev,
        [ticker]: { actual_eps: '', actual_revenue: '' },
      }));
    } catch (err) {
      console.error('Validation error', err);
      alert(`Failed to record actuals: ${(Array.isArray(err.response?.data?.detail) ? err.response.data.detail.map(e => e.msg || String(e)).join('; ') : err.response?.data?.detail) || err.message}`);
    } finally {
      setValidationLoading((prev) => ({ ...prev, [ticker]: false }));
    }
  };

  const handleExportCsv = () => {
    if (!filteredEarnings.length) {
      alert('Nothing to export for this date');
      return;
    }
    const headers = ['Ticker', 'Company', 'Sector', 'Time', 'Consensus EPS', 'Predicted EPS', 'Surprise %', 'Prediction', 'Confidence', 'Validated'];
    const rows = filteredEarnings.map((earning) => [
      earning.ticker,
      earning.company,
      earning.sector,
      earning.earnings_time,
      earning.consensus_eps ?? 'N/A',
      earning.predicted_eps ?? 'N/A',
      earning.predicted_surprise_pct ?? 'N/A',
      earning.prediction_direction,
      earning.confidence_score ?? 'N/A',
      earning.validated ? 'Yes' : 'No',
    ]);
    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `earnings_${formatDateKey(selectedDate)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getConfidenceBadge = (confidence) => {
    if (!confidence) return <span className="text-gray-500">N/A</span>;
    
    const level = confidence > 70 ? 'HIGH' : confidence > 40 ? 'MED' : 'LOW';
    const color = confidence > 70 ? 'text-green-600' : confidence > 40 ? 'text-yellow-600' : 'text-red-600';
    
    return <span className={`font-semibold ${color}`}>{level} ({confidence.toFixed(0)}%)</span>;
  };

  const getDataQualityBadge = (earning) => {
    if (earning.validated) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800" title="Actual results recorded">
          ✅ Validated
        </span>
      );
    }

    const sourceQuality = (earning.data_source || earning.data_type || '').toLowerCase();
    if (sourceQuality.includes('sample')) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-700" title="Simulated sample data - awaiting confirmed earnings date">
          SAMPLE
        </span>
      );
    }

    const quarters = earning.quarters_analyzed || 0;
    if (quarters >= 6) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800" title={`High quality - ${quarters} quarters analyzed`}>
          ✓ HIGH ({quarters}q)
        </span>
      );
    }
    if (quarters >= 3) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800" title={`Medium quality - ${quarters} quarters analyzed`}>
          ~ MED ({quarters}q)
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-orange-100 text-orange-800" title="Limited data (less than 3 quarters)">
        ! LOW
      </span>
    );
  };

  const filteredEarnings = earnings.filter((earning) => {
    const matchesSearch = 
      earning.ticker?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      earning.company?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesSector = filters.sector === 'all' || earning.sector === filters.sector;
    
    const matchesMarketCap = filters.marketCap === 'all' || 
      (filters.marketCap === 'large' && earning.market_cap > 10000000000) ||
      (filters.marketCap === 'mid' && earning.market_cap > 2000000000 && earning.market_cap <= 10000000000) ||
      (filters.marketCap === 'small' && earning.market_cap <= 2000000000);
    
    const matchesConfidence = filters.confidence === 'all' ||
      (filters.confidence === 'high' && earning.confidence_score > 70) ||
      (filters.confidence === 'med' && earning.confidence_score > 40 && earning.confidence_score <= 70) ||
      (filters.confidence === 'low' && earning.confidence_score <= 40);

    const sourceQuality = (earning.data_source || earning.data_quality || '').toLowerCase();
    const matchesQuality = filters.dataQuality === 'all' || sourceQuality.includes(filters.dataQuality);

    const matchesValidated = filters.validated === 'all' ||
      (filters.validated === 'validated' && earning.validated) ||
      (filters.validated === 'pending' && !earning.validated);

    const matchesWatchlist = !filters.watchlistOnly || watchlist.has(earning.ticker);
    
    return matchesSearch && matchesSector && matchesMarketCap && matchesConfidence && matchesQuality && matchesValidated && matchesWatchlist;
  });

  const bgClass = darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900';
  const cardClass = darkMode ? 'bg-gray-800' : 'bg-white';
  const inputClass = darkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';
  const helperTextClass = darkMode ? 'text-gray-300' : 'text-gray-600';
  const accuracyButtonClass = showAccuracy
    ? 'bg-purple-600 text-white hover:bg-purple-700'
    : (darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300');
  const modeToggleClass = darkMode
    ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
    : 'bg-gray-200 text-gray-800 hover:bg-gray-300';
  const headerTitle = null;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <AppHeader
        title={headerTitle}
        isAdmin={isAdmin}
        onShowScanner={onShowScanner}
        onShowEarnings={onShowEarnings}
        onShowTastytrade={onShowTastytrade}
        onShowAdmin={onShowAdmin}
        onLogout={onLogout}
      />
      <div className={`flex-1 ${bgClass}`}>
        <div className="max-w-7xl mx-auto p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
            <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">
              {t('earnings_label')}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowAccuracy(!showAccuracy);
                  if (!showAccuracy && !accuracyData) fetchAccuracyData();
                }}
                className={`px-4 py-2 rounded-lg transition ${accuracyButtonClass}`}
                title="View prediction accuracy statistics"
              >
                📈 {showAccuracy ? 'Hide' : 'Show'} Accuracy
              </button>
              <button
                onClick={() => setDarkMode(!darkMode)}
                className={`p-2 rounded-lg transition ${modeToggleClass}`}
                title={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
              >
                {darkMode ? '🌞' : '🌙'}
              </button>
            </div>
          </div>

        {/* Accuracy Dashboard */}
        {showAccuracy && accuracyData && (
          <div className={`${cardClass} rounded-lg shadow-lg p-6 mb-6`}>
            <h2 className="text-xl font-bold mb-4">📊 Prediction Accuracy Statistics (Last {accuracyData.period_days} Days)</h2>
            
            {accuracyData.total_predictions === 0 ? (
              <p className="text-gray-500">No validated predictions yet. Predictions will be validated after earnings are reported.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Overall Accuracy */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900 dark:to-blue-800 p-4 rounded-lg">
                  <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">Overall Accuracy</div>
                  <div className="text-3xl font-bold text-blue-600 dark:text-blue-300">{accuracyData.overall_accuracy}%</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {accuracyData.correct_predictions} / {accuracyData.total_predictions} predictions
                  </div>
                </div>

                {/* By Direction */}
                <div className={`${cardClass} p-4 rounded-lg border`}>
                  <div className="text-sm font-semibold mb-2">Accuracy by Direction</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-600">BEAT ({accuracyData.accuracy_by_direction.BEAT.count}):</span>
                      <span className="font-semibold">{accuracyData.accuracy_by_direction.BEAT.accuracy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">MISS ({accuracyData.accuracy_by_direction.MISS.count}):</span>
                      <span className="font-semibold">{accuracyData.accuracy_by_direction.MISS.accuracy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-yellow-600">INLINE ({accuracyData.accuracy_by_direction.INLINE.count}):</span>
                      <span className="font-semibold">{accuracyData.accuracy_by_direction.INLINE.accuracy}%</span>
                    </div>
                  </div>
                </div>

                {/* By Confidence */}
                <div className={`${cardClass} p-4 rounded-lg border`}>
                  <div className="text-sm font-semibold mb-2">Calibration by Confidence</div>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-green-600">HIGH ({accuracyData.accuracy_by_confidence['HIGH (>70%)'].count}):</span>
                      <span className="font-semibold">{accuracyData.accuracy_by_confidence['HIGH (>70%)'].accuracy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-yellow-600">MED ({accuracyData.accuracy_by_confidence['MEDIUM (40-70%)'].count}):</span>
                      <span className="font-semibold">{accuracyData.accuracy_by_confidence['MEDIUM (40-70%)'].accuracy}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">LOW ({accuracyData.accuracy_by_confidence['LOW (<40%)'].count}):</span>
                      <span className="font-semibold">{accuracyData.accuracy_by_confidence['LOW (<40%)'].accuracy}%</span>
                    </div>
                  </div>
                </div>

                {/* By Sector */}
                {Object.keys(accuracyData.accuracy_by_sector).length > 0 && (
                  <div className={`${cardClass} p-4 rounded-lg border md:col-span-2 lg:col-span-3`}>
                    <div className="text-sm font-semibold mb-2">Accuracy by Sector</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
                      {Object.entries(accuracyData.accuracy_by_sector).map(([sector, accuracy]) => (
                        <div key={sector} className="flex justify-between items-center p-2 bg-gray-50 dark:bg-gray-700 rounded">
                          <span className="truncate mr-2">{sector}:</span>
                          <span className="font-semibold">{accuracy}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Data Quality Banner */}
        {earnings.length > 0 && earnings.some(e => e.data_quality === 'limited' || e.quarters_analyzed === 0) && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Data Quality Notice:</strong> Some predictions have limited historical data. 
                  Predictions with more historical quarters analyzed (shown in company details) are generally more reliable. 
                  Check the "quarters analyzed" metric for data quality.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
          <p className="text-sm text-yellow-800">
            ⚠️ <strong>Disclaimer:</strong> For educational use only. Not financial advice. 
            Predictions use real historical earnings data from Yahoo Finance when available. 
            Accuracy varies based on data quality and market conditions. Past performance does not guarantee future results.
          </p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-4">
            <p className="text-sm text-red-800">
              ❌ <strong>Error:</strong> {errorMessage}
            </p>
          </div>
        )}

        {/* Date Navigation */}
        <div className={`${cardClass} rounded-lg shadow p-4 mb-4`}>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handlePreviousDay}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="View earnings from the previous day"
            >
              ← Previous Day
            </button>

            <input
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(new Date(e.target.value))}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Select a specific date to view earnings calendar"
            />

            <button
              onClick={handleNextDay}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              title="View earnings from the next day"
            >
              Next Day →
            </button>

            <button
              onClick={() => setSelectedDate(new Date())}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              title="Jump back to today's earnings calendar"
            >
              Today
            </button>

            <button
              onClick={fetchEarningsCalendar}
              disabled={loading}
              className="ml-auto px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400"
              title="Refresh earnings data for the selected date"
            >
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {summaryStats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            <div className={`${cardClass} rounded-lg shadow p-4`}>
              <p className="text-sm text-gray-500">Upcoming Earnings</p>
              <p className="text-2xl font-bold">{summaryStats.total}</p>
              <p className="text-xs text-gray-400">Across {timelineDays.length} day window</p>
            </div>
            <div className={`${cardClass} rounded-lg shadow p-4`}>
              <p className="text-sm text-gray-500">High Confidence</p>
              <p className="text-2xl font-bold text-green-600">{summaryStats.highConfidence}</p>
              <p className="text-xs text-gray-400">≥70% probability</p>
            </div>
            <div className={`${cardClass} rounded-lg shadow p-4`}>
              <p className="text-sm text-gray-500">Pending Validation</p>
              <p className="text-2xl font-bold text-yellow-600">{summaryStats.pendingValidation}</p>
              <p className="text-xs text-gray-400">Need actual EPS</p>
            </div>
            <div className={`${cardClass} rounded-lg shadow p-4`}>
              <p className="text-sm text-gray-500">Avg Beat Probability</p>
              <p className="text-2xl font-bold text-blue-600">{summaryStats.averageBeat}%</p>
              <div className="text-xs text-gray-400 mt-1">
                Top sectors: {summaryStats.topSectors.map((sector) => `${sector.sector} (${sector.count})`).join(', ') || 'N/A'}
              </div>
            </div>
          </div>
        )}

        {timelineDays.length > 0 && (
          <div className={`${cardClass} rounded-lg shadow p-4 mb-4`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold">7-Day Earnings Strip</p>
              <p className="text-xs text-gray-500">Tap a card to jump to that day</p>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {timelineDays.map((day) => {
                const isSelected = formatDateKey(selectedDate) === day.key;
                return (
                  <button
                    key={day.key}
                    onClick={() => setSelectedDate(new Date(day.date))}
                    className={`min-w-[140px] border rounded-lg p-3 text-left transition ${
                      isSelected ? 'bg-blue-600 text-white border-blue-600' : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="text-xs uppercase tracking-wide">{day.isToday ? 'Today' : day.label}</div>
                    <div className="text-xl font-bold">{day.total}</div>
                    <div className="text-xs mt-1">
                      {day.highConfidence} high conviction · {day.validated} validated
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Data Quality Notice */}
        {earnings.length > 0 && (
          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4 rounded">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-800">
                  <strong>Data Quality:</strong> Earnings dates are verified using multiple Yahoo Finance sources. 
                  <strong className="text-green-700"> HIGH</strong> = 2+ sources agree, 
                  <strong className="text-yellow-700"> MEDIUM</strong> = multiple sources with some disagreement, 
                  <strong className="text-orange-700"> LOW</strong> = single source only. 
                  {earnings.some(e => e.data_type === 'sample') && (
                    <strong className="text-gray-700"> SAMPLE</strong>
                  )}
                  {earnings.some(e => e.data_type === 'sample') && ' indicates simulated data when real earnings data is unavailable.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search and Filters */}
        <div className={`${cardClass} rounded-lg shadow p-4 mb-4`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <input
              type="text"
              placeholder="Search ticker or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Type to search by ticker symbol (e.g., AAPL) or company name (e.g., Apple)"
            />

            <select
              value={filters.sector}
              onChange={(e) => setFilters({ ...filters, sector: e.target.value })}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Filter companies by business sector"
            >
              <option value="all">All Sectors</option>
              {availableSectors.map((sector) => (
                <option key={sector} value={sector}>
                  {sector}
                </option>
              ))}
            </select>

            <select
              value={filters.marketCap}
              onChange={(e) => setFilters({ ...filters, marketCap: e.target.value })}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Filter by company size: Large Cap >$10B, Mid Cap $2-10B, Small Cap <$2B"
            >
              <option value="all">All Market Cap</option>
              <option value="large">Large Cap (&gt;$10B)</option>
              <option value="mid">Mid Cap ($2B-$10B)</option>
              <option value="small">Small Cap (&lt;$2B)</option>
            </select>

            <select
              value={filters.confidence}
              onChange={(e) => setFilters({ ...filters, confidence: e.target.value })}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Filter by prediction confidence level: High >70%, Medium 40-70%, Low <40%"
            >
              <option value="all">All Confidence</option>
              <option value="high">High (&gt;70%)</option>
              <option value="med">Medium (40-70%)</option>
              <option value="low">Low (&lt;40%)</option>
            </select>

            <select
              value={filters.dataQuality}
              onChange={(e) => setFilters({ ...filters, dataQuality: e.target.value })}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Filter by data quality level"
            >
              <option value="all">All Data Quality</option>
              <option value="real">High (Real)</option>
              <option value="limited">Limited</option>
              <option value="sample">Sample</option>
            </select>

            <select
              value={filters.validated}
              onChange={(e) => setFilters({ ...filters, validated: e.target.value })}
              className={`px-4 py-2 border rounded-lg ${inputClass}`}
              title="Filter by validation status"
            >
              <option value="all">All Status</option>
              <option value="pending">Awaiting Actuals</option>
              <option value="validated">Validated</option>
            </select>
          </div>

          <div className="mt-4 flex flex-wrap gap-3 justify-between">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setFilters({ ...filters, watchlistOnly: !filters.watchlistOnly })}
                className={`px-4 py-2 rounded-lg border transition ${
                  filters.watchlistOnly ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300'
                }`}
                title="Show only companies saved to your watchlist"
              >
                {filters.watchlistOnly ? '★ Watchlist Only' : '☆ All Companies'}
              </button>
            </div>
            <button
              onClick={handleExportCsv}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              ⬇️ Export CSV
            </button>
          </div>
        </div>

        {/* Earnings Table */}
        <div className={`${cardClass} rounded-lg shadow overflow-hidden`}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Stock ticker symbol">
                    Ticker
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Full company name">
                    Company
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Business sector (Technology, Financial, Healthcare, etc.)">
                    Sector
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Expected reporting time: BMO = before market open, AMC = after market close">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Analyst consensus EPS estimate - average of all analyst predictions">
                    Consensus EPS
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Our AI-predicted EPS based on historical performance and trends">
                    Predicted EPS
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Expected surprise % - how much we predict the actual result will differ from consensus">
                    Surprise %
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="BEAT = likely to exceed expectations, MISS = likely to fall short, INLINE = likely to meet expectations">
                    Prediction
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Confidence level in our prediction: HIGH (>70%), MEDIUM (40-70%), LOW (<40%)">
                    Confidence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Data quality indicator: HIGH = 2+ sources agree, MEDIUM = multiple sources, LOW = 1 source, SAMPLE = simulated data">
                    Data Quality
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="Validation status for this prediction">
                    Validation
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium uppercase" title="View detailed historical earnings and prediction explanation">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="12" className="px-4 py-8 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : filteredEarnings.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                      {searchQuery ? 'No earnings found matching your search' : 'No earnings scheduled for this date'}
                    </td>
                  </tr>
                ) : (
                  filteredEarnings.map((earning) => {
                    const isExpanded = expandedRows.has(earning.ticker);
                    const validationValues = validationInputs[earning.ticker] || { actual_eps: '', actual_revenue: '' };
                    return (
                      <React.Fragment key={earning.ticker}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                          <td className="px-4 py-3 font-semibold text-blue-600">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => toggleRowExpansion(earning.ticker)}
                                className="text-sm text-gray-500 hover:text-blue-600"
                                title="Show inline insights"
                              >
                                {isExpanded ? '▾' : '▸'}
                              </button>
                              <button
                                onClick={() => toggleWatchlistTicker(earning.ticker)}
                                className={`text-lg ${watchlist.has(earning.ticker) ? 'text-yellow-500' : 'text-gray-400'} hover:text-yellow-500`}
                                title={watchlist.has(earning.ticker) ? 'Remove from watchlist' : 'Add to watchlist'}
                              >
                                {watchlist.has(earning.ticker) ? '★' : '☆'}
                              </button>
                              <span title={`${earning.ticker} stock ticker`}>{earning.ticker}</span>
                            </div>
                          </td>
                      <td className="px-4 py-3 text-sm" title={earning.company || 'N/A'}>
                        {earning.company || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm" title={`Business sector: ${earning.sector || 'N/A'}`}>
                        {earning.sector || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm" title="Earnings release timing">
                        {earning.earnings_time || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm" title={`Wall Street analysts expect earnings of ${earning.consensus_eps ? `$${earning.consensus_eps.toFixed(2)}` : 'N/A'} per share`}>
                        {earning.consensus_eps ? `$${earning.consensus_eps.toFixed(2)}` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm" title={`Our AI model predicts earnings of ${earning.predicted_eps ? `$${earning.predicted_eps.toFixed(2)}` : 'N/A'} per share`}>
                        {earning.predicted_eps ? `$${earning.predicted_eps.toFixed(2)}` : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm" title={`Expected to ${earning.predicted_surprise_pct > 0 ? 'beat' : earning.predicted_surprise_pct < 0 ? 'miss' : 'meet'} consensus by ${earning.predicted_surprise_pct ? `${earning.predicted_surprise_pct.toFixed(1)}%` : 'N/A'}`}>
                        {earning.predicted_surprise_pct ? `${earning.predicted_surprise_pct.toFixed(1)}%` : 'N/A'}
                      </td>
                      <td className="px-4 py-3" title={`Prediction: ${earning.prediction_direction || 'Unknown'} with ${earning.beat_probability ? `${earning.beat_probability.toFixed(0)}%` : 'N/A'} probability`}>
                        {getPredictionBadge(earning.prediction_direction, earning.beat_probability)}
                      </td>
                      <td className="px-4 py-3 text-sm" title={`Confidence in this prediction is ${earning.confidence_score ? `${earning.confidence_score.toFixed(0)}%` : 'N/A'} based on historical accuracy`}>
                        {getConfidenceBadge(earning.confidence_score)}
                      </td>
                      <td className="px-4 py-3 text-sm" title={earning.sources ? `Data sources: ${earning.sources}` : 'Data quality indicator'}>
                        {getDataQualityBadge(earning)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {earning.validated ? (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800" title="Actual EPS recorded">
                            ✓ Validated
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-semibold bg-yellow-100 text-yellow-700" title="Awaiting actual EPS">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => fetchCompanyDetails(earning.ticker)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          title={`View detailed earnings history and analysis for ${earning.ticker}`}
                        >
                          View Details
                        </button>
                      </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan="12" className="bg-gray-50 dark:bg-gray-900/30 px-4 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div>
                                  <p className="text-xs text-gray-500">Quarters Analyzed</p>
                                  <p className="text-lg font-semibold">{earning.quarters_analyzed || 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Historical Beat Rate</p>
                                  <p className="text-lg font-semibold">{earning.historical_beat_rate ? `${earning.historical_beat_rate.toFixed(1)}%` : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">3M Price Momentum</p>
                                  <p className={`text-lg font-semibold ${((earning.price_change_pct || 0) >= 0) ? 'text-green-600' : 'text-red-600'}`}>
                                    {earning.price_change_pct != null ? `${earning.price_change_pct.toFixed(1)}%` : 'N/A'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Average Surprise</p>
                                  <p className="text-lg font-semibold">{earning.avg_surprise_pct ? `${earning.avg_surprise_pct.toFixed(1)}%` : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Predicted Surprise</p>
                                  <p className="text-lg font-semibold">{earning.predicted_surprise_pct ? `${earning.predicted_surprise_pct.toFixed(1)}%` : 'N/A'}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-gray-500">Last EPS</p>
                                  <p className="text-lg font-semibold">{earning.last_eps ? `$${earning.last_eps.toFixed(2)}` : 'N/A'}</p>
                                </div>
                              </div>

                              <div className="mt-4 flex flex-wrap gap-3">
                                <button
                                  onClick={() => fetchCompanyDetails(earning.ticker)}
                                  className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm"
                                >
                                  Open Detailed Modal
                                </button>
                                <button
                                  onClick={() => toggleWatchlistTicker(earning.ticker)}
                                  className="px-3 py-2 border rounded-lg text-sm"
                                >
                                  {watchlist.has(earning.ticker) ? 'Remove from Watchlist' : 'Save to Watchlist'}
                                </button>
                              </div>

                              {!earning.validated ? (
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  <div>
                                    <label className="text-xs text-gray-500">Actual EPS</label>
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={validationValues.actual_eps || ''}
                                      onChange={(e) => handleValidationInputChange(earning.ticker, 'actual_eps', e.target.value)}
                                      className={`mt-1 w-full px-3 py-2 border rounded-lg ${inputClass}`}
                                      placeholder="e.g. 2.15"
                                    />
                                  </div>
                                  <div>
                                    <label className="text-xs text-gray-500">Actual Revenue (optional)</label>
                                    <input
                                      type="number"
                                      step="1000000"
                                      value={validationValues.actual_revenue || ''}
                                      onChange={(e) => handleValidationInputChange(earning.ticker, 'actual_revenue', e.target.value)}
                                      className={`mt-1 w-full px-3 py-2 border rounded-lg ${inputClass}`}
                                      placeholder="e.g. 5000000000"
                                    />
                                  </div>
                                  <div className="flex items-end">
                                    <button
                                      onClick={() => handleValidationSubmit(earning.ticker)}
                                      className="w-full px-3 py-2 bg-green-600 text-white rounded-lg"
                                      disabled={validationLoading[earning.ticker]}
                                    >
                                      {validationLoading[earning.ticker] ? 'Saving...' : 'Submit Actuals'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div className="mt-4 text-sm text-green-700">
                                  Actual EPS: {earning.actual_eps != null ? `$${earning.actual_eps.toFixed(2)}` : 'N/A'} · Surprise: {earning.actual_surprise_pct != null ? `${earning.actual_surprise_pct.toFixed(1)}%` : 'N/A'} · Prediction {earning.prediction_correct ? 'correct' : 'missed'}
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Company Details Modal */}
        {selectedCompany && companyDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={`${cardClass} rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto`}>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h2 className="text-2xl font-bold">{companyDetails.ticker}</h2>
                    <p className="text-gray-600">{companyDetails.company}</p>
                  </div>
                  <button
                    onClick={() => setSelectedCompany(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                {/* Prediction Summary */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Prediction</p>
                    <p className="text-lg font-bold">
                      {companyDetails.prediction?.prediction_direction || 'N/A'}
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Beat Probability</p>
                    <p className="text-lg font-bold">
                      {companyDetails.prediction?.beat_probability?.toFixed(0)}%
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Confidence</p>
                    <p className="text-lg font-bold">
                      {companyDetails.prediction?.confidence_score?.toFixed(0)}%
                    </p>
                  </div>
                  <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-100'}`}>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Data Quality</p>
                    <p className="text-lg font-bold text-blue-600">
                      {companyDetails.prediction?.quarters_analyzed || 0} Qtrs
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {companyDetails.prediction?.data_quality === 'real' ? '✓ Real Data' : '⚠ Limited'}
                    </p>
                  </div>
                </div>

                {/* Historical Performance */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3">Historical Earnings (Last 8 Quarters)</h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Reported EPS</th>
                          <th className="px-3 py-2 text-left">Estimated EPS</th>
                          <th className="px-3 py-2 text-left">Surprise %</th>
                          <th className="px-3 py-2 text-left">Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {companyDetails.history?.length > 0 ? (
                          companyDetails.history.map((h, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="px-3 py-2">{new Date(h.date).toLocaleDateString()}</td>
                              <td className="px-3 py-2">${h.reported_eps?.toFixed(2)}</td>
                              <td className="px-3 py-2">${h.estimated_eps?.toFixed(2)}</td>
                              <td className={`px-3 py-2 font-semibold ${h.surprise_pct > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {h.surprise_pct?.toFixed(1)}%
                              </td>
                              <td className="px-3 py-2">
                                {h.beat ? (
                                  <span className="text-green-600 font-semibold">✓ Beat</span>
                                ) : (
                                  <span className="text-red-600 font-semibold">✗ Miss</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="px-3 py-4 text-center text-gray-500">
                              No historical data available
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Explanation */}
                <div className={`p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                  <h4 className="font-semibold mb-2">How We Predict:</h4>
                  <ul className="text-sm space-y-1">
                    <li>• Historical beat rate: {companyDetails.beat_rate?.toFixed(0)}%</li>
                    <li>• Average surprise: {companyDetails.avg_surprise?.toFixed(1)}%</li>
                    <li>• Recent price momentum: {companyDetails.prediction?.price_change_3m?.toFixed(1)}%</li>
                    <li>• Analyst consensus incorporated</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
  );
};

export default EarningsPrediction;
